import type { LevelAssessmentModel } from "@shared/labs/level-assessment-model";
import type { ScoredCandidate } from "@shared/labs/sourcing";
import { Loader2, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { rpcClient } from "@/lib/rpc";
import type { LaScopeSelection } from "./model-picker";

const NONE = "__none__";

/**
 * "Create a Level-Assessment evaluation set from these candidates." The selected
 * sourcing candidates already carry a LinkedIn URL + name; this dialog gathers
 * the LA scope (model + discipline, prefilled from the search's own scope when it
 * had one) plus a name/reference, then calls the EXISTING `levelAssessmentSetCreate`
 * RPC. LA re-fetches each profile and runs its canonical per-candidate rater — the
 * normal, explicit cost of an evaluation set (bounded to ≤20), nothing automatic.
 */
export function CreateSetDialog({
  open,
  onOpenChange,
  candidates,
  prefillScope,
  defaultName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The candidates the recruiter ticked (already capped at 20 upstream). */
  candidates: ScoredCandidate[];
  /** The search's LA scope, used to prefill model + discipline. */
  prefillScope: LaScopeSelection;
  /** Suggested set name (e.g. derived from the brief); editable. */
  defaultName: string;
}) {
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const [models, setModels] = useState<LevelAssessmentModel[] | null>(null);
  const [loadError, setLoadError] = useState(false);

  const [modelId, setModelId] = useState<string | null>(prefillScope.modelId);
  const [disciplineId, setDisciplineId] = useState<string | null>(prefillScope.disciplineId);
  const [name, setName] = useState(defaultName);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Re-seed from the props each time the dialog opens (a new selection / search).
  useEffect(() => {
    if (!open) return;
    setModelId(prefillScope.modelId);
    setDisciplineId(prefillScope.disciplineId);
    setName(defaultName);
    setNote("");
    setFormError(null);
  }, [open, prefillScope.modelId, prefillScope.disciplineId, defaultName]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = (await rpcClient.labs.levelAssessmentModelsList({})) as {
          models: LevelAssessmentModel[];
        };
        if (!cancelled) setModels(res.models);
      } catch {
        if (!cancelled) setLoadError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedModel = modelId ? (models?.find((m) => m.id === modelId) ?? null) : null;
  const disciplines = selectedModel?.disciplines ?? [];
  const needsDiscipline = disciplines.length > 0;

  const inScope = useMemo(() => {
    if (!selectedModel) return [] as string[];
    const cross = selectedModel.crossAxes.map((a) => a.label);
    const vert = disciplineId
      ? (disciplines.find((d) => d.id === disciplineId)?.axes.map((a) => a.label) ?? [])
      : [];
    return [...cross, ...vert];
  }, [selectedModel, disciplineId, disciplines]);

  async function onCreate() {
    setFormError(null);
    const trimmedName = name.trim();
    if (!trimmedName) return setFormError("Dê um nome ao pacote.");
    if (!modelId) return setFormError("Escolha um modelo de nível.");
    if (needsDiscipline && !disciplineId) return setFormError("Escolha uma disciplina.");
    if (candidates.length === 0) return setFormError("Nenhum candidato selecionado.");

    setSubmitting(true);
    try {
      const res = (await rpcClient.labs.levelAssessmentSetCreate({
        name: trimmedName,
        modelId,
        disciplineId: needsDiscipline ? disciplineId : undefined,
        note: note.trim() || undefined,
        profiles: candidates.map((c) => ({
          linkedinUrl: c.url,
          candidateName: c.name || undefined,
        })),
      })) as { set: { id: string } };
      toast({
        title: "Pacote criado",
        description: `Avaliando ${candidates.length} candidatos no Level Assessment…`,
      });
      navigate(`/labs/level-assessment/sets/${res.set.id}`);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Falha ao criar o pacote.");
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Avaliar no Level Assessment</DialogTitle>
          <DialogDescription>
            Cria um pacote de candidatos no Level Assessment com os{" "}
            <span className="font-medium text-foreground">{candidates.length}</span> perfis
            selecionados. O modelo estima o nível de cada um — isso aciona o avaliador do LA (uma
            avaliação completa por candidato).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <label className="block space-y-1.5">
            <span className="text-[12px] font-medium text-foreground">Nome *</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="ex. Forward Deployed · Sourcing"
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-[12px] font-medium text-foreground">
              Referência <span className="text-muted-foreground">(opcional)</span>
            </span>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="ex. #FD-204"
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </label>

          <div className="flex flex-wrap items-end gap-2.5">
            <label className="flex-1 space-y-1.5">
              <span className="text-[12px] font-medium text-foreground">Modelo de nível *</span>
              <Select
                value={modelId ?? NONE}
                onValueChange={(v) => {
                  setModelId(v === NONE ? null : v);
                  setDisciplineId(null);
                }}
                disabled={models === null}
              >
                <SelectTrigger className="h-9 text-[13px]">
                  <SelectValue placeholder={models === null ? "Carregando…" : "Escolher modelo"} />
                </SelectTrigger>
                <SelectContent>
                  {(models ?? []).map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>

            {selectedModel && needsDiscipline && (
              <label className="flex-1 space-y-1.5">
                <span className="text-[12px] font-medium text-foreground">Disciplina *</span>
                <Select
                  value={disciplineId ?? NONE}
                  onValueChange={(v) => setDisciplineId(v === NONE ? null : v)}
                >
                  <SelectTrigger className="h-9 text-[13px]">
                    <SelectValue placeholder="Escolher disciplina" />
                  </SelectTrigger>
                  <SelectContent>
                    {disciplines.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
            )}
          </div>

          {selectedModel && inScope.length > 0 && (
            <p className="flex items-start gap-2 text-[12px] leading-relaxed text-muted-foreground">
              <Sparkles size={13} className="mt-0.5 shrink-0 opacity-70" />
              <span>
                Avaliando as competências:{" "}
                <span className="text-foreground">{inScope.join(", ")}</span>.
              </span>
            </p>
          )}

          {loadError && (
            <p className="text-[12px] text-destructive">
              Não foi possível carregar os modelos de nível.
            </p>
          )}
          {formError && <p className="text-[12px] text-destructive">{formError}</p>}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button size="sm" onClick={onCreate} disabled={submitting || models === null}>
            {submitting ? <Loader2 className="animate-spin" /> : <Sparkles />}
            {submitting ? "Criando…" : `Avaliar ${candidates.length} candidatos`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
